import {
    addDays,
    addMonths,
    differenceInCalendarDays,
    getDaysInMonth,
    isValid,
    parseISO,
    setDate,
    startOfMonth,
} from "date-fns";
import { toIsoDate } from "@common/helpers/date";
import { pluralizeWithCount } from "@common/helpers/string";

// Standard invoice payment terms a user can pick from a dropdown (PRO-11287).
// These drive due-date auto-population and are persisted on native invoice
// detail so templates can derive a new due date from a new invoice date.
export enum NativeInvoiceTermType {
    ON_RECEIPT = "on_receipt",
    NET_15 = "net_15",
    NET_30 = "net_30",
    NET_60 = "net_60",
    NET_90 = "net_90",
    CUSTOM = "custom",
}

export const NATIVE_INVOICE_TERM_TYPES = [
    NativeInvoiceTermType.ON_RECEIPT,
    NativeInvoiceTermType.NET_15,
    NativeInvoiceTermType.NET_30,
    NativeInvoiceTermType.NET_60,
    NativeInvoiceTermType.NET_90,
    NativeInvoiceTermType.CUSTOM,
] as const;

export function isNativeInvoiceTermType(
    value: string,
): value is NativeInvoiceTermType {
    return NATIVE_INVOICE_TERM_TYPES.some((termType) => termType === value);
}

export const NATIVE_INVOICE_TERM_TYPE_LABELS: Record<
    NativeInvoiceTermType,
    string
> = {
    [NativeInvoiceTermType.ON_RECEIPT]: "On receipt",
    [NativeInvoiceTermType.NET_15]: "Net 15",
    [NativeInvoiceTermType.NET_30]: "Net 30",
    [NativeInvoiceTermType.NET_60]: "Net 60",
    [NativeInvoiceTermType.NET_90]: "Net 90",
    [NativeInvoiceTermType.CUSTOM]: "Custom",
};

// Custom-term sub-types, set via a modal (mirrors QBO's custom terms options).
export enum NativeInvoiceCustomTermType {
    DUE_IN_DAYS = "due_in_days",
    DUE_BY_DAY_OF_CURRENT_MONTH = "due_by_day_of_current_month",
    DUE_BY_DAY_OF_NEXT_MONTH = "due_by_day_of_next_month",
}

export const NATIVE_INVOICE_CUSTOM_TERM_TYPES = [
    NativeInvoiceCustomTermType.DUE_IN_DAYS,
    NativeInvoiceCustomTermType.DUE_BY_DAY_OF_CURRENT_MONTH,
    NativeInvoiceCustomTermType.DUE_BY_DAY_OF_NEXT_MONTH,
] as const;

export interface NativeInvoiceCustomTermValue {
    type: NativeInvoiceCustomTermType;
    // Populated for DUE_IN_DAYS.
    days: number | null;
    // Populated for DUE_BY_DAY_OF_CURRENT_MONTH / DUE_BY_DAY_OF_NEXT_MONTH.
    dayOfMonth: number | null;
}

// Ordinal suffix for a day-of-month (1st, 2nd, 3rd, 4th ... 21st, 22nd).
function ordinalDay(day: number): string {
    const lastTwoDigits = day % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return `${day}th`;
    }
    switch (day % 10) {
        case 1:
            return `${day}st`;
        case 2:
            return `${day}nd`;
        case 3:
            return `${day}rd`;
        default:
            return `${day}th`;
    }
}

// Descriptive label for a custom term rule (e.g. "Due in 45 days" or "Due by
// the 15th of the next month") so the printed Terms field carries the actual
// rule rather than a bare "Custom". Falls back to the plain "Custom" label when
// the rule can't be resolved to a concrete value.
function resolveCustomTermLabel(
    customTerm: NativeInvoiceCustomTermValue | null | undefined,
): string {
    const customLabel =
        NATIVE_INVOICE_TERM_TYPE_LABELS[NativeInvoiceTermType.CUSTOM];
    if (!customTerm) {
        return customLabel;
    }
    switch (customTerm.type) {
        case NativeInvoiceCustomTermType.DUE_IN_DAYS:
            if (customTerm.days == null || customTerm.days < 0) {
                return customLabel;
            }
            return `Due in ${pluralizeWithCount("day", customTerm.days)}`;
        case NativeInvoiceCustomTermType.DUE_BY_DAY_OF_CURRENT_MONTH:
            if (customTerm.dayOfMonth == null) {
                return customLabel;
            }
            return `Due by the ${ordinalDay(customTerm.dayOfMonth)} of the current month`;
        case NativeInvoiceCustomTermType.DUE_BY_DAY_OF_NEXT_MONTH:
            if (customTerm.dayOfMonth == null) {
                return customLabel;
            }
            return `Due by the ${ordinalDay(customTerm.dayOfMonth)} of the next month`;
        default:
            return customLabel;
    }
}

interface ResolveNativeInvoiceTermLabelParams {
    termType: NativeInvoiceTermType | null | undefined;
    customTerm?: NativeInvoiceCustomTermValue | null;
}

// The human-readable Terms value printed on the invoice PDF's Payment info grid
// (PRO-12178), resolving a full term rule (standard term or custom rule) to its
// display string - e.g. "Net 30", "Due in 45 days". A Custom selection is
// expanded into its underlying rule instead of printing a bare "Custom". Null
// when no term has been selected yet, matching the printed-field convention
// elsewhere (an unset value renders no info-block line rather than a blank one).
export function resolveNativeInvoiceTermLabel({
    termType,
    customTerm,
}: ResolveNativeInvoiceTermLabelParams): string | null {
    if (!termType) {
        return null;
    }
    if (termType !== NativeInvoiceTermType.CUSTOM) {
        return NATIVE_INVOICE_TERM_TYPE_LABELS[termType];
    }
    return resolveCustomTermLabel(customTerm);
}

const NET_DAYS_BY_TERM_TYPE: Partial<Record<NativeInvoiceTermType, number>> = {
    [NativeInvoiceTermType.NET_15]: 15,
    [NativeInvoiceTermType.NET_30]: 30,
    [NativeInvoiceTermType.NET_60]: 60,
    [NativeInvoiceTermType.NET_90]: 90,
};

const STANDARD_NET_TERM_TYPES = [
    NativeInvoiceTermType.NET_15,
    NativeInvoiceTermType.NET_30,
    NativeInvoiceTermType.NET_60,
    NativeInvoiceTermType.NET_90,
] as const;

// Clamps a requested day-of-month into the valid range for the given month
// (e.g. day 31 in a 30-day month becomes the last day of that month).
function clampDayOfMonth(referenceDate: Date, dayOfMonth: number): number {
    const daysInMonth = getDaysInMonth(referenceDate);
    return Math.min(Math.max(dayOfMonth, 1), daysInMonth);
}

function deriveDueDateFromCustomTerm(
    base: Date,
    customTerm: NativeInvoiceCustomTermValue | null | undefined,
): string | null {
    if (!customTerm) {
        return null;
    }

    switch (customTerm.type) {
        case NativeInvoiceCustomTermType.DUE_IN_DAYS: {
            if (customTerm.days == null || customTerm.days < 0) {
                return null;
            }
            return toIsoDate(addDays(base, customTerm.days));
        }
        case NativeInvoiceCustomTermType.DUE_BY_DAY_OF_CURRENT_MONTH: {
            if (customTerm.dayOfMonth == null) {
                return null;
            }
            const monthStart = startOfMonth(base);
            const day = clampDayOfMonth(monthStart, customTerm.dayOfMonth);
            return toIsoDate(setDate(monthStart, day));
        }
        case NativeInvoiceCustomTermType.DUE_BY_DAY_OF_NEXT_MONTH: {
            if (customTerm.dayOfMonth == null) {
                return null;
            }
            const nextMonthStart = startOfMonth(addMonths(base, 1));
            const day = clampDayOfMonth(nextMonthStart, customTerm.dayOfMonth);
            return toIsoDate(setDate(nextMonthStart, day));
        }
        default:
            return null;
    }
}

interface DeriveDueDateFromTermSelectionParams {
    invoiceDate: string | null;
    termType: NativeInvoiceTermType;
    customTerm?: NativeInvoiceCustomTermValue | null;
}

// Derives a YYYY-MM-DD due date from the invoice date and a structured term
// selection (standard term or custom rule). Pure calendar-day math — terms
// count calendar days and never shift for weekends. Returns null when the
// invoice date or the (custom) term inputs can't be resolved to a sane date;
// callers should leave the existing due date untouched in that case.
export function deriveDueDateFromTermSelection({
    invoiceDate,
    termType,
    customTerm,
}: DeriveDueDateFromTermSelectionParams): string | null {
    if (!invoiceDate) {
        return null;
    }

    const base = parseISO(invoiceDate);
    if (!isValid(base)) {
        return null;
    }

    if (termType === NativeInvoiceTermType.ON_RECEIPT) {
        return toIsoDate(base);
    }

    if (termType === NativeInvoiceTermType.CUSTOM) {
        return deriveDueDateFromCustomTerm(base, customTerm);
    }

    const days = NET_DAYS_BY_TERM_TYPE[termType];
    if (days === undefined) {
        return null;
    }
    return toIsoDate(addDays(base, days));
}

export interface NativeInvoiceDueDateRuleColumns {
    termType: NativeInvoiceTermType | null;
    customTermType: NativeInvoiceCustomTermType | null;
    customTermDays: number | null;
    customTermDayOfMonth: number | null;
}

export interface NativeInvoiceDueDateRule {
    termType: NativeInvoiceTermType | null;
    customTerm: NativeInvoiceCustomTermValue | null;
}

export function toDueDateRuleColumns({
    termType,
    customTerm,
}: NativeInvoiceDueDateRule): NativeInvoiceDueDateRuleColumns {
    if (termType == null) {
        return {
            termType: null,
            customTermType: null,
            customTermDays: null,
            customTermDayOfMonth: null,
        };
    }
    if (termType !== NativeInvoiceTermType.CUSTOM) {
        return {
            termType,
            customTermType: null,
            customTermDays: null,
            customTermDayOfMonth: null,
        };
    }
    return {
        termType,
        customTermType: customTerm?.type ?? null,
        customTermDays: customTerm?.days ?? null,
        customTermDayOfMonth: customTerm?.dayOfMonth ?? null,
    };
}

export function areDueDateRuleColumnsEqual(
    a: NativeInvoiceDueDateRuleColumns,
    b: NativeInvoiceDueDateRuleColumns,
): boolean {
    return (
        a.termType === b.termType &&
        a.customTermType === b.customTermType &&
        a.customTermDays === b.customTermDays &&
        a.customTermDayOfMonth === b.customTermDayOfMonth
    );
}

export function fromDueDateRuleColumns({
    termType,
    customTermType,
    customTermDays,
    customTermDayOfMonth,
}: NativeInvoiceDueDateRuleColumns): NativeInvoiceDueDateRule {
    if (termType !== NativeInvoiceTermType.CUSTOM || customTermType == null) {
        return { termType, customTerm: null };
    }
    return {
        termType,
        customTerm: {
            type: customTermType,
            days: customTermDays,
            dayOfMonth: customTermDayOfMonth,
        },
    };
}

interface DeriveTermSelectionFromDueDateParams {
    invoiceDate: string | null;
    dueDate: string | null;
}

function standardTermTypeForDays(days: number): NativeInvoiceTermType | null {
    if (days === 0) {
        return NativeInvoiceTermType.ON_RECEIPT;
    }
    for (const termType of STANDARD_NET_TERM_TYPES) {
        if (NET_DAYS_BY_TERM_TYPE[termType] === days) {
            return termType;
        }
    }
    return null;
}

// Inverse of deriveDueDateFromTermSelection: a manual due-date pick maps back
// to On receipt / Net 15|30|60|90 when the calendar-day gap matches, otherwise
// to a custom "due in N days" rule. Returns null when the dates can't be
// resolved so callers leave the existing term selection untouched.
export function deriveTermSelectionFromDueDate({
    invoiceDate,
    dueDate,
}: DeriveTermSelectionFromDueDateParams): NativeInvoiceDueDateRule | null {
    if (!invoiceDate || !dueDate) {
        return null;
    }

    const invoice = parseISO(invoiceDate);
    const due = parseISO(dueDate);
    if (!isValid(invoice) || !isValid(due)) {
        return null;
    }

    return deriveTermSelectionFromDays(differenceInCalendarDays(due, invoice));
}

export function deriveTermSelectionFromDays(
    days: number,
): NativeInvoiceDueDateRule | null {
    if (days < 0) {
        return null;
    }

    const standardTermType = standardTermTypeForDays(days);
    if (standardTermType != null) {
        return { termType: standardTermType, customTerm: null };
    }

    return {
        termType: NativeInvoiceTermType.CUSTOM,
        customTerm: {
            type: NativeInvoiceCustomTermType.DUE_IN_DAYS,
            days,
            dayOfMonth: null,
        },
    };
}
